import type { PreContractIntakeForm, PreContractProduct } from './preContractTypes';
import { PRE_CONTRACT_PRODUCTS } from './preContractTypes';

export type WizardStepId = 'welcome' | 'products' | 'site' | 'property' | 'utility' | 'design' | 'review';

export const WIZARD_STEPS: { id: WizardStepId; label: string; short: string }[] = [
    { id: 'welcome', label: 'Welcome', short: 'Start' },
    { id: 'products', label: 'Products', short: 'Products' },
    { id: 'site', label: 'Site address', short: 'Site' },
    { id: 'property', label: 'Property', short: 'Property' },
    { id: 'utility', label: 'Utility', short: 'Utility' },
    { id: 'design', label: 'Design setup', short: 'Design' },
    { id: 'review', label: 'Review & launch', short: 'Review' },
];

export function selectedProducts(form: PreContractIntakeForm): PreContractProduct[] {
    return PRE_CONTRACT_PRODUCTS.filter((p) => form.products[p]);
}

export function formatInstallAddress(form: PreContractIntakeForm): string {
    return [form.installStreet, form.installCity, form.installState, form.installZip]
        .map((p) => p.trim())
        .filter(Boolean)
        .join(', ');
}

export function isStepValid(step: WizardStepId, form: PreContractIntakeForm): boolean {
    switch (step) {
        case 'welcome':
            return true;
        case 'products':
            return selectedProducts(form).length > 0;
        case 'site':
            return !!(
                form.installStreet.trim() &&
                form.installCity.trim() &&
                form.installState &&
                form.installZip.trim()
            );
        case 'property':
            return true;
        case 'utility':
            return !!(form.utilityCompany && form.annualUsageKwh.trim() && form.utilityRate.trim());
        case 'design': {
            const anyRoof = form.roofSelections.some(Boolean);
            return (
                anyRoof &&
                designAvailabilityIssues(form).length === 0 &&
                roofSquaresComplete(form) &&
                batterySelectionComplete(form)
            );
        }
        case 'review':
            return canLaunchWorkspace(form);
        default:
            return false;
    }
}

export function designAvailabilityIssues(form: PreContractIntakeForm): string[] {
    const checked = selectedProducts(form);
    const missing: string[] = [];
    const hasAurora = form.preferredDesignTool === 'aurora' && form.auroraAvailable;
    const hasHover = form.preferredDesignTool === 'hover' && form.hoverAvailable;
    const hasDesignTool = hasAurora || hasHover;

    if (!hasDesignTool) {
        missing.push('Select an available design tool (Aurora or Hover)');
        return missing;
    }
    if (checked.includes('Solar') && !hasAurora) {
        missing.push('Solar requires Aurora');
    }
    if (checked.includes('Roof') && !(hasAurora || hasHover)) {
        missing.push('Roof requires Aurora or Hover');
    }
    if (checked.includes('RRR') && !(hasAurora || hasHover)) {
        missing.push('RRR requires Aurora or Hover');
    }
    return missing;
}

export function hasActiveDesignTool(form: PreContractIntakeForm): boolean {
    return (
        (form.preferredDesignTool === 'aurora' && form.auroraAvailable) ||
        (form.preferredDesignTool === 'hover' && form.hoverAvailable)
    );
}

export function effectiveRoofSlopeSquares(form: PreContractIntakeForm): {
    low: number;
    normal: number;
    total: number;
} {
    const low = Number(form.roofSlopeLowOverride ?? form.roofSlopeLowTotal) || 0;
    const normal = Number(form.roofSlopeNormalOverride ?? form.roofSlopeNormalTotal) || 0;
    return { low, normal, total: low + normal };
}

/** Solar/Roof deals need squares > 0; if calculated totals are 0, overrides are required. */
export function roofSquaresComplete(form: PreContractIntakeForm): boolean {
    if (!form.products.Solar && !form.products.Roof) return true;
    const calculated =
        (Number(form.roofSlopeLowTotal) || 0) + (Number(form.roofSlopeNormalTotal) || 0);
    const { total } = effectiveRoofSlopeSquares(form);
    if (total <= 0) return false;
    if (calculated <= 0) {
        return form.roofSlopeLowOverride != null || form.roofSlopeNormalOverride != null;
    }
    return true;
}

export function batterySelectionComplete(form: PreContractIntakeForm): boolean {
    if (!form.products.Battery) return true;
    return Boolean(
        form.batteryManufacturer.trim() && form.batteryName.trim() && form.batteryQuantity > 0,
    );
}

export function canRunAiDesigns(form: PreContractIntakeForm): boolean {
    const requiredSteps: WizardStepId[] = ['products', 'site', 'utility', 'design'];
    return (
        requiredSteps.every((s) => isStepValid(s, form)) &&
        designAvailabilityIssues(form).length === 0
    );
}

export function canLaunchWorkspace(form: PreContractIntakeForm): boolean {
    const requiredSteps: WizardStepId[] = ['products', 'site', 'utility', 'design'];
    return (
        requiredSteps.every((s) => isStepValid(s, form)) &&
        designAvailabilityIssues(form).length === 0 &&
        form.offsetPreference != null
    );
}

export const CONTENT_STEPS: { id: WizardStepId; label: string }[] = [
    { id: 'products', label: 'Products' },
    { id: 'site', label: 'Site address' },
    { id: 'property', label: 'Property title' },
    { id: 'utility', label: 'Utility' },
    { id: 'design', label: 'Design setup' },
];

export function stepSummary(
    step: WizardStepId,
    form: PreContractIntakeForm,
    customerName: string,
): string {
    switch (step) {
        case 'products':
            return selectedProducts(form).join(', ') || 'None selected';
        case 'site':
            return formatInstallAddress(form) || 'Not entered';
        case 'property':
            return `${customerName} · Single Family Residential`;
        case 'utility':
            return [form.utilityCompany, form.annualUsageKwh ? `${form.annualUsageKwh} kWh/yr` : '']
                .filter(Boolean)
                .join(' · ') || 'Not entered';
        case 'design': {
            const parts: string[] = [];
            parts.push(`${form.roofSelections.filter(Boolean).length} roofs`);
            if (form.offsetPreference) {
                const prefLabel =
                    form.offsetPreference === 'full' ? 'Full offset' : 'Balanced';
                parts.push(prefLabel);
            }
            return parts.join(' · ') || 'Not configured';
        }
        default:
            return '';
    }
}

export function completedStepCount(form: PreContractIntakeForm): number {
    return CONTENT_STEPS.filter((s) => isStepValid(s.id, form)).length;
}

export function furthestUnlockedStep(form: PreContractIntakeForm): number {
    for (let i = 0; i < CONTENT_STEPS.length; i++) {
        if (!isStepValid(CONTENT_STEPS[i].id, form)) {
            return i;
        }
    }
    return CONTENT_STEPS.length - 1;
}

export type StepValidationStatus = 'complete' | 'incomplete' | 'error';

export type StepValidation = {
    status: StepValidationStatus;
    label: string;
    issues: string[];
};

export function getStepValidation(step: WizardStepId, form: PreContractIntakeForm): StepValidation {
    const issues: string[] = [];

    switch (step) {
        case 'products':
            if (!selectedProducts(form).length) {
                issues.push('Select at least one product');
            }
            break;
        case 'site':
            if (!form.installStreet.trim()) issues.push('Street address is required');
            if (!form.installCity.trim()) issues.push('City is required');
            if (!form.installState) issues.push('State is required');
            if (!form.installZip.trim()) issues.push('ZIP code is required');
            break;
        case 'property':
            return { status: 'complete', label: 'Verified', issues: [] };
        case 'utility':
            if (!form.utilityCompany) issues.push('Utility company is required');
            if (!form.annualUsageKwh.trim()) issues.push('Annual usage is required');
            if (!form.utilityRate.trim()) issues.push('All-in rate is required');
            break;
        case 'design': {
            if (!form.roofSelections.some(Boolean)) {
                issues.push('Select at least one roof');
            }
            if (!roofSquaresComplete(form)) {
                issues.push('Roof squares cannot be 0 — enter override values');
            }
            if (!batterySelectionComplete(form)) {
                issues.push('Battery manufacturer, name, and quantity are required');
            }
            const toolIssues = designAvailabilityIssues(form);
            if (toolIssues.length > 0) {
                return {
                    status: 'error',
                    label: 'Issue',
                    issues: [...issues, ...toolIssues],
                };
            }
            break;
        }
        default:
            break;
    }

    if (issues.length === 0) {
        return { status: 'complete', label: 'Complete', issues: [] };
    }

    return { status: 'incomplete', label: 'Required', issues };
}

export function canAdvanceFromIntakeBoard(
    form: PreContractIntakeForm,
    titleReady: boolean,
): boolean {
    return (
        titleReady &&
        getStepValidation('utility', form).status === 'complete' &&
        getStepValidation('design', form).status === 'complete'
    );
}

export function stepsNeedingAttention(form: PreContractIntakeForm): WizardStepId[] {
    return CONTENT_STEPS.filter((step) => getStepValidation(step.id, form).status !== 'complete').map(
        (step) => step.id,
    );
}
